import { useEffect, useState, type RefObject } from 'react';
import type { Breakpoint } from '../types';

// Deliberately container-width-based (NOT Tailwind viewport breakpoints) —
// this screen is shared/embedded with customers and garage users, so layout
// must react to the hosting container's width rather than the viewport.
// Matches the design reference mockup's own breakpoint logic.
const MOBILE_MAX_WIDTH = 640;
const TABLET_MAX_WIDTH = 1024;

function widthToBreakpoint(width: number): Breakpoint {
  if (width < MOBILE_MAX_WIDTH) return 'mobile';
  if (width < TABLET_MAX_WIDTH) return 'tablet';
  return 'desktop';
}

/**
 * Derives a `Breakpoint` from the width of the element referenced by `ref`,
 * updating whenever a `ResizeObserver` reports a size change.
 */
export function useBreakpoint(ref: RefObject<HTMLElement | null>): Breakpoint {
  const [breakpoint, setBreakpoint] = useState<Breakpoint>('desktop');

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      setBreakpoint(widthToBreakpoint(entry.contentRect.width));
    });

    observer.observe(node);

    return () => observer.disconnect();
  }, [ref]);

  return breakpoint;
}
